/**
 * Message Utilities - WhatsApp-style message grouping and formatting
 */

/**
 * Get the date group label for a message
 * @param {Date} messageDate - The date of the message
 * @returns {string} - "Today", "Yesterday", or formatted date (e.g., "May 14, 2026")
 */
export const getDateGroupLabel = (messageDate) => {
    const msgDate = new Date(messageDate);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const msgDateOnly = new Date(msgDate.getFullYear(), msgDate.getMonth(), msgDate.getDate());
    const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const yesterdayDateOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

    if (msgDateOnly.getTime() === todayDateOnly.getTime()) {
        return 'Today';
    }
    
    if (msgDateOnly.getTime() === yesterdayDateOnly.getTime()) {
        return 'Yesterday';
    }

    // Format: "May 14, 2026"
    return msgDate.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    });
};

/**
 * Group messages by date
 * @param {Array} messages - Array of messages to group
 * @returns {Array} - Array of objects with format { dateGroup, messages: [] }
 */
export const groupMessagesByDate = (messages) => {
    if (!Array.isArray(messages) || messages.length === 0) {
        return [];
    }

    const grouped = {};

    messages.forEach(message => {
        const messageDate = message.timestamp || message.created_at;
        if (!messageDate) return;

        const dateLabel = getDateGroupLabel(messageDate);

        if (!grouped[dateLabel]) {
            grouped[dateLabel] = {
                dateGroup: dateLabel,
                messages: [],
                timestamp: new Date(messageDate).getTime() // For sorting
            };
        }

        grouped[dateLabel].messages.push(message);
    });

    // Convert to array and sort by date
    return Object.values(grouped).sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Format sidebar timestamp (contact list)
 * Follows WhatsApp rules:
 * - Today: "10:45 AM"
 * - Yesterday: "Yesterday"
 * - Current year: "May 20"
 * - Older: "05/12/2025"
 * @param {string|Date} dateValue - The date to format
 * @returns {string} - Formatted timestamp
 */
export const formatSidebarTimestamp = (dateValue) => {
    if (!dateValue) return '';

    const date = new Date(dateValue);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const yesterdayDateOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

    // Today - show time only
    if (dateOnly.getTime() === todayDateOnly.getTime()) {
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    }

    // Yesterday
    if (dateOnly.getTime() === yesterdayDateOnly.getTime()) {
        return 'Yesterday';
    }

    // Current year - show month/day
    if (date.getFullYear() === today.getFullYear()) {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    // Older - show full date
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
};

/**
 * Format message timestamp (inline in chat)
 * @param {string|Date} dateValue - The date to format
 * @returns {string} - Formatted time (e.g., "2:30 PM")
 */
export const formatMessageTime = (dateValue) => {
    if (!dateValue) return '';

    const date = new Date(dateValue);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
};

/**
 * Get the first unread message index in a list
 * @param {Array} messages - Array of messages
 * @returns {number} - Index of first unread message, or -1 if all read
 */
export const getFirstUnreadMessageIndex = (messages) => {
    return messages.findIndex((msg) => {
        const isIncoming = msg.direction === 'INCOMING' || msg.sender === 'customer' || msg.sender === 'received';
        const isRead = Boolean(msg.read || msg.is_read || msg.read_at || String(msg.status || '').toLowerCase() === 'read');
        return isIncoming && !isRead;
    });
};

/**
 * Determine if messages have any unread messages
 * @param {Array} messages - Array of messages
 * @returns {boolean} - True if there are unread messages
 */
export const hasUnreadMessages = (messages) => {
    return getFirstUnreadMessageIndex(messages) !== -1;
};

/**
 * Calculate scroll position for smart scrolling
 * Cases:
 * 1. Has unread: scroll to first unread
 * 2. No unread: scroll to latest message
 * @param {Array} messages - Array of messages
 * @param {number} containerHeight - Height of the chat container
 * @param {number} messageHeight - Average height of a message (estimate)
 * @returns {number} - Scroll position in pixels
 */
export const calculateSmartScrollPosition = (messages, containerHeight = 500, messageHeight = 60) => {
    const unreadIndex = getFirstUnreadMessageIndex(messages);
    
    if (unreadIndex !== -1) {
        // Has unread messages - scroll to first unread
        // Add some buffer to show context before unread
        const bufferMessages = Math.max(0, unreadIndex - 3);
        return bufferMessages * messageHeight;
    }

    // No unread - scroll to bottom
    return (messages.length * messageHeight) - containerHeight;
};

/**
 * Get messages that should be marked as read (visible in viewport)
 * @param {Array} messages - All messages
 * @param {HTMLElement} containerRef - Chat container element
 * @param {number} threshold - Pixels from bottom to consider as visible
 * @returns {Array} - Array of message IDs that are visible
 */
export const getVisibleUnreadMessages = (messages, containerRef, threshold = 100) => {
    if (!containerRef) return [];

    const visibleMessages = [];
    const containerRect = containerRef.getBoundingClientRect();
    const containerBottom = containerRect.bottom;

    messages.forEach(msg => {
        const isIncoming = msg.direction === 'INCOMING' || msg.sender === 'customer' || msg.sender === 'received';
        const isRead = Boolean(msg.read || msg.is_read || msg.read_at || String(msg.status || '').toLowerCase() === 'read');
        if (!isRead && isIncoming) {
            // In a real implementation, you would check if the message element
            // is within the viewport using getBoundingClientRect()
            // For now, we'll mark all unread as visible when container is scrolled near bottom
            const scrollPercentage = (containerRef.scrollTop + containerRef.clientHeight) / containerRef.scrollHeight;
            if (scrollPercentage > 0.5) {
                visibleMessages.push(msg.id);
            }
        }
    });

    return visibleMessages;
};

/**
 * Format relative time (e.g., "2m ago", "just now")
 * @param {string|Date} dateValue - The date to format
 * @returns {string} - Relative time string
 */
export const formatRelativeTime = (dateValue) => {
    if (!dateValue) return '';

    const date = new Date(dateValue);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return formatSidebarTimestamp(dateValue);
};

/**
 * Get unread message count for a contact
 * @param {Array} messages - Array of messages
 * @returns {number} - Count of unread messages
 */
export const getUnreadCount = (messages) => {
    return messages.filter((msg) => {
        const isIncoming = msg.direction === 'INCOMING' || msg.sender === 'customer' || msg.sender === 'received';
        const isRead = Boolean(msg.read || msg.is_read || msg.read_at || String(msg.status || '').toLowerCase() === 'read');
        return isIncoming && !isRead;
    }).length;
};
